import { ResolvedTokens, alpha, mix } from '@porter/shared-theme';

/**
 * Booking-site surface tokens. The defaults below are the pre-customizer
 * values, kept for non-booking-site pages and while a theme resolves;
 * `themedSurface` overrides them on every booking site page.
 *
 * Single source of truth — the `:root` block and the themed block are both
 * generated from here, so they cannot drift.
 */
export const SURFACE_DEFAULTS = {
  '--surface': '#ffffff',
  '--surface-panel': '#ffffff',
  '--surface-fg': '#333333',
  '--surface-dim': '#666666',
  '--surface-faint': '#939393',
  // Three border weights, lightest first. Themed they all collapse to
  // tokens.line, so only these defaults tell them apart: `line` separates bands
  // of the page, `divider` is the hairline around a card or under a header, and
  // `outline` draws a control the customer is meant to act on.
  '--surface-line': '#f5f5f5',
  '--surface-divider': 'rgba(0, 0, 0, 0.12)',
  '--surface-outline': '#939393',
  '--surface-raised': '#f5f5f5',
  '--surface-on-raised': '#333333',
  '--surface-selected': '#ffd6ce',
  '--surface-on-selected': '#ff3008',
  '--surface-bar': '#ffffff',
  '--surface-on-bar': '#333333',
  '--surface-accent': '#ff3008',
  '--surface-primary': '#ff3008',
  '--surface-on-primary': '#ffffff',
  '--surface-primary-edge': '1px solid #ff3008',
  '--surface-primary-shadow': '0 10px 40px -10px rgb(255 48 8 / 50%)',
  // Matches the themed value: the primary CTA's hover background is now the
  // resting color, so with no filter it would have no hover state at all.
  '--surface-hover-filter': 'brightness(0.92)',
  '--surface-chip-bg': '#ffd6ce',
  '--surface-on-accent': '#ffffff',
  '--surface-card': '#ffffff',
  '--surface-heading': '#333333',
  '--surface-head-track': '0em',
  '--surface-chip-border': 'none',
  // Spec 7. A themed page must render body copy in the selected family (or
  // Inter behind a display face) — a hardcoded Montserrat silently overrides it.
  // Unthemed is the Porter default theme, whose font is Montserrat, so heads
  // and body match until a shop picks otherwise.
  '--surface-font-head': "'Montserrat', sans-serif",
  '--surface-font-body': "'Montserrat', sans-serif",
  // Black artwork that has to stay black in its source — flat <img> icons,
  // which cannot inherit a colour, and signature ink bound for a white PDF.
  '--surface-ink-filter': 'none',
} as const;

type SurfaceVar = keyof typeof SURFACE_DEFAULTS;

/**
 * Distributes over the union. Inlining the conditional would check the whole
 * union against the template at once, which is false, collapsing to never.
 */
type StripPrefix<T> = T extends `--surface-${infer R}` ? R : never;

/** 'fg' | 'dim' | 'line' | … — the token name without the `--surface-` prefix. */
export type SurfaceToken = StripPrefix<SurfaceVar>;

/** Typed reference to a token. A typo is a compile error, not a blank colour. */
export function surface(token: SurfaceToken): string {
  return `var(--surface-${token})`;
}

/** The page background itself, which has no suffix. */
export const surfaceBg = 'var(--surface)';

/**
 * Tokens that own a paired foreground. Spreading a pair is the only supported
 * way to set one of these backgrounds, so a surface can never be painted
 * without the text colour it was contrast-checked against.
 */
type PairedToken = Extract<
  SurfaceToken,
  'raised' | 'selected' | 'bar' | 'primary'
>;

export function surfacePair(token: PairedToken) {
  return {
    background: `var(--surface-${token})`,
    color: `var(--surface-on-${token})`,
  } as const;
}

/** Themed overrides. Keys are checked against the same union as the defaults. */
export function themedSurface(
  tokens: ResolvedTokens,
): Record<SurfaceVar, string> {
  const isDark = tokens.fg === '#FFFFFF';
  /** A single hex means the background is one flat colour. */
  const isFlat = /^#[0-9a-f]{6}$/i.test(tokens.bg.trim());
  /** Opaque lift off the page, shared by raised panels and fixed bars. */
  const raised = mix(tokens.base, tokens.fg, isDark ? 0.08 : 0.06);
  return {
    '--surface': tokens.bg,
    // Page containers let the themed background through rather than stacking
    // an opaque slab on top of it.
    '--surface-panel': 'transparent',
    '--surface-fg': tokens.fg,
    '--surface-dim': tokens.dim,
    '--surface-faint': tokens.dim,
    '--surface-line': tokens.line,
    '--surface-divider': tokens.line,
    '--surface-outline': tokens.line,
    // Opaque, not an alpha overlay: raised surfaces sit on top of other things
    // (the progress line runs behind the step circles), and anything showing
    // through reads as a rendering fault.
    '--surface-raised': raised,
    '--surface-on-raised': tokens.fg,
    // Selected state belongs to the accent (spec 5), held at low alpha so the
    // paired foreground stays readable.
    '--surface-selected': alpha(tokens.accent, 0.18),
    '--surface-on-selected': tokens.accent,
    // Flat backgrounds reuse the page colour exactly, so a fixed bar is
    // seamless. Anything with a gradient or texture gets the same opaque
    // elevated tone as menus and dialogs: repainting the pattern inside a bar
    // restarts it, and a translucent scrim washes the page colour out.
    '--surface-bar': isFlat ? tokens.bg : raised,
    '--surface-on-bar': tokens.fg,
    '--surface-accent': tokens.accent,
    '--surface-primary': tokens.primary,
    '--surface-on-primary': tokens.onPrimary,
    '--surface-primary-edge': tokens.primaryEdge,
    // The stock CTA glow is tinted Porter red — wrong under any other primary.
    '--surface-primary-shadow': 'none',
    '--surface-hover-filter': 'brightness(0.92)',
    // Chips use primary.light unthemed, which MUI derives from primary.main —
    // near-invisible once primary is a pale token.
    // Spec: a chip is the text color at a fixed low alpha, so it reads as a
    // surface on any background without a second stored color.
    '--surface-chip-bg': tokens.chip,
    '--surface-chip-border': 'none',
    '--surface-on-accent': tokens.onAccent,
    '--surface-card': tokens.card,
    // Rule 6.5 already decided whether the primary is readable here.
    '--surface-heading': tokens.heading,
    '--surface-head-track': tokens.headTrack,
    '--surface-font-head': tokens.headFont,
    '--surface-font-body': tokens.bodyFont,
    '--surface-ink-filter': isDark ? 'invert(1)' : 'none',
  };
}
